import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Navbar from '../components/Navbar'
import ListingCard from '../components/ListingCard'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

const REGIONS   = ['All Regions','North America','South America','Europe','Africa & Middle East','Caribbean','Pacific & Oceania','Asia']
const DISCS     = ['All','Windsurf','Kite','Wingfoil','Windfoil','Multi']
const CATS      = ['All','Club','School','Shop','Association','Online']

// Unsplash fallback images per discipline
const DISC_IMG = {
  windsurf: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=600&q=80',
  kite:     'https://images.unsplash.com/photo-1527004013197-933a4b244bcd?w=600&q=80',
  wing:     'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80',
  windfoil: 'https://images.unsplash.com/photo-1471922694854-ff1b63b20054?w=600&q=80',
  multi:    'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=600&q=80',
}

// Static seed data (shown while Supabase loads or as fallback)
const SEED = [
]

export default function DirectoryPage() {
  const { user } = useAuth()
  const [listings, setListings] = useState(SEED)
  const [loading, setLoading]   = useState(true)
  const [region, setRegion]     = useState('All Regions')
  const [disc, setDisc]         = useState('All')
  const [cat, setCat]           = useState('All')
  const [search, setSearch]     = useState('')

  useEffect(() => {
    fetchListings()
  }, [user])

  async function fetchListings() {
    const { data, error } = await supabase
      .from('listings')
      .select('*')
      .eq('status', 'approved')
      .order('is_featured', { ascending: false })
      .order('vote_count', { ascending: false })

    if (data && data.length > 0) {
      // Fetch which ones the user has voted on
      if (user) {
        const { data: userVotes } = await supabase
          .from('votes').select('listing_id').eq('user_id', user.id)
        const votedIds = new Set((userVotes || []).map(v => v.listing_id))
        setListings(data.map(l => ({ ...l, user_voted: votedIds.has(l.id) })))
      } else {
        setListings(data)
      }
    }
    // else keep seed data
    setLoading(false)
  }

  const filtered = listings.filter(l => {
    if (region !== 'All Regions' && l.region !== region) return false
    if (disc !== 'All' && l.discipline?.toLowerCase() !== disc.toLowerCase()) return false
    if (cat  !== 'All' && l.category?.toLowerCase() !== cat.toLowerCase()) return false
    if (search) {
      const q = search.toLowerCase()
      if (!`${l.name} ${l.city} ${l.country} ${l.description}`.toLowerCase().includes(q)) return false
    }
    return true
  })

  // Top 3 by vote count
  const top3 = [...listings]
    .filter(l => l.status === 'approved')
    .sort((a,b) => (b.vote_count||0) - (a.vote_count||0))
    .slice(0, 3)

  const MEDALS = ['🥇','🥈','🥉']

  return (
    <div>
      <Navbar transparent />

      {/* HERO */}
      <div style={{ position:'relative', height:320, overflow:'hidden', background:'var(--navy)' }}>
        <div style={{ position:'absolute', inset:0, background:"url('https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=1400&q=80') center/cover", opacity:.4 }} />
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(to bottom, rgba(13,31,53,.2), rgba(13,31,53,.7))' }} />
        <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'0 24px', paddingTop:72 }}>
          <img src="/logo.png" alt="Wind Sports Directory" style={{ height:72, width:'auto', filter:'brightness(0) invert(1)', marginBottom:16, opacity:.9 }} />
          <h1 style={{ color:'#fff', fontSize:'clamp(24px,5vw,44px)', fontWeight:800, letterSpacing:'-.5px', textShadow:'0 2px 16px rgba(0,0,0,.5)', marginBottom:10 }}>
            Global Wind Sports Directory
          </h1>
          <p style={{ color:'rgba(255,255,255,.75)', fontSize:15, fontWeight:300 }}>
            Windsurfing · Kiteboarding · Wingfoil · Windfoil
          </p>
          {/* Discipline legend pills — scroll horizontally on mobile */}
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            <style>{`.disc-scroll::-webkit-scrollbar{display:none}`}</style>
            {[
              ['windsurf','#0369a1','https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=80&q=60','Windsurf'],
              ['kite',    '#7c3aed','https://images.unsplash.com/photo-1527004013197-933a4b244bcd?w=80&q=60','Kite'],
              ['wing',    '#059669','https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=80&q=60','Wingfoil'],
              ['windfoil','#b45309','https://images.unsplash.com/photo-1471922694854-ff1b63b20054?w=80&q=60','Windfoil'],
              ['multi',   '#be123c','https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=80&q=60','Multi'],
            ].map(([d,c,img,label]) => (
              <span key={d} style={{ display:'inline-flex', alignItems:'center', gap:6, background:`${c}cc`, border:'1.5px solid rgba(255,255,255,.25)', color:'#fff', fontFamily:'var(--mono)', fontSize:10, fontWeight:600, padding:'4px 10px 4px 4px', borderRadius:50, letterSpacing:1, textTransform:'uppercase', overflow:'hidden' }}>
                <img src={img} alt={label} style={{ width:22, height:22, borderRadius:'50%', objectFit:'cover', border:'1.5px solid rgba(255,255,255,.4)' }} />
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* TOP 3 STRIP */}
      {top3.length > 0 && (
        <div style={{ background:'var(--navy)', borderBottom:'1px solid rgba(255,255,255,.08)', padding:'10px 20px' }}>
          <div style={{ maxWidth:1020, margin:'0 auto' }}>
            {/* Label — always its own full-width row */}
            <div style={{ fontFamily:'var(--mono)', fontSize:10, fontWeight:700, color:'rgba(255,255,255,.35)', letterSpacing:2, textTransform:'uppercase', marginBottom:8 }}>
              Top Voted
            </div>
            {/* Entries — horizontal row on desktop, vertical stack on mobile via CSS */}
            <div className='top3-strip' style={{ display:'flex', alignItems:'center', gap:0, flexWrap:'wrap' }}>
              {top3.map((l, i) => (
                <div key={l.id} className='top3-item' style={{ display:'flex', alignItems:'center', gap:8, paddingRight:20, marginRight:20, borderRight: i < top3.length-1 ? '1px solid rgba(255,255,255,.1)' : 'none' }}>
                  <span style={{ fontSize:14 }}>{MEDALS[i]}</span>
                  <div>
                    <div style={{ fontSize:12, fontWeight:700, color:'#fff' }}>{l.name}</div>
                    <div style={{ fontSize:10, color:'rgba(255,255,255,.4)', fontFamily:'var(--mono)' }}>
                      {l.city}{l.country ? `, ${l.country}` : ''} · ▲ {l.vote_count}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SEARCH & FILTERS */}
      <div style={{ background:'#eef2f8', padding:'20px 20px 16px' }}>
        {/* Search bar */}
        <div style={{ maxWidth:680, margin:'0 auto 14px', background:'#fff', borderRadius:50, display:'flex', alignItems:'center', padding:'0 8px 0 20px', boxShadow:'0 2px 12px rgba(0,0,0,.1)', height:50 }}>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, city, or country…"
            style={{ flex:1, border:'none', outline:'none', fontSize:14, color:'var(--gray-700)', background:'none' }} />
          <div style={{ width:36, height:36, background:'var(--blue)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </div>
        </div>

        {/* Filter rows */}
        {[
          { label:'Region', opts: REGIONS, val: region, set: setRegion },
          { label:'Category', opts: CATS, val: cat, set: setCat },
          { label:'Discipline', opts: DISCS, val: disc, set: setDisc },
        ].map(({ label, opts, val, set }) => (
         <div key={label} style={{ maxWidth:680, margin:'0 auto 8px' }}>
            <div style={{ fontFamily:'var(--mono)', fontSize:10, fontWeight:600, color:'var(--gray-500)', letterSpacing:1, textTransform:'uppercase', marginBottom:6 }}>{label}</div>
            <div style={{ display:'flex', gap:6, flexWrap:'nowrap', overflowX:'auto', paddingBottom:2, scrollbarWidth:'none' }}>
              {opts.map(o => (
                <button key={o} onClick={() => set(o)}
                  style={{
                    fontSize:12, fontWeight:500, padding:'4px 13px', borderRadius:50,
                    border:`1.5px solid ${val===o ? 'var(--blue)' : 'var(--gray-200)'}`,
                    background: val===o ? 'var(--blue)' : '#fff',
                    color: val===o ? '#fff' : 'var(--gray-500)',
                    cursor:'pointer', transition:'all .15s', fontFamily:'var(--font)',
                    flexShrink: 0,
                  }}>
                  {o}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* RESULTS */}
      <div style={{ maxWidth:1020, margin:'0 auto', padding:'20px 20px 12px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:8 }}>
        <div style={{ fontSize:20, fontWeight:800, letterSpacing:'-.3px' }}>
          {filtered.length} <span style={{ fontWeight:400, color:'var(--gray-500)', fontSize:16 }}>results</span>
        </div>
        <Link to="/submit" className="btn btn-primary" style={{ fontSize:13 }}>+ Submit a Listing</Link>
      </div>

      {/* CARDS */}
      <div style={{ maxWidth:1020, margin:'0 auto', padding:'0 20px 80px' }}>
    {/* ── FEATURED SECTION ── */}
    {(() => {
      const featuredListings = filtered.filter(l => l.is_featured)
      if (!featuredListings.length) return null
      return (
        <div style={{ marginBottom: 8 }}>
          {/* Featured header */}
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
            <div style={{ background:'linear-gradient(135deg, var(--gold), #f59e0b)', borderRadius:8, padding:'6px 14px', display:'flex', alignItems:'center', gap:7 }}>
              <span style={{ fontSize:15 }}>⭐</span>
              <span style={{ fontFamily:'var(--mono)', fontSize:11, fontWeight:700, color:'#fff', letterSpacing:'1.5px', textTransform:'uppercase' }}>
                Featured This Month
              </span>
            </div>
            <span style={{ fontSize:12, color:'var(--gray-400)', fontFamily:'var(--mono)' }}>
              Monthly vote winners &amp; sponsored listings
            </span>
          </div>

          {/* Featured cards row */}
          <div className='cards-grid' style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:18, marginBottom:0 }}>
            {featuredListings.map(l => <ListingCard key={`feat-${l.id}`} listing={l} />)}
          </div>

          {/* Divider into main directory */}
          <div style={{ borderBottom:'2px solid var(--gray-200)', margin:'28px 0 0', paddingBottom:0 }}>
            <span style={{ fontFamily:'var(--mono)', fontSize:11, fontWeight:700, color:'var(--gray-400)', letterSpacing:2, textTransform:'uppercase', display:'inline-block', paddingBottom:10 }}>
              All Listings
            </span>
          </div>
        </div>
      )
    })()}

    {/* ── REGION GROUPS ── */}
        {loading && <p style={{ textAlign:'center', color:'var(--gray-400)', padding:'60px 0' }}>Loading listings…</p>}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign:'center', padding:'80px 20px' }}>
            <div style={{ fontSize:44, marginBottom:14 }}>🌬️</div>
            <h3 style={{ fontSize:20, fontWeight:700, color:'var(--gray-700)' }}>No results found</h3>
            <p style={{ color:'var(--gray-400)', marginTop:6 }}>Try different filters or clear the search</p>
          </div>
        )}

        {/* Group by region */}
        {REGIONS.slice(1).map(r => {
          const regionListings = filtered.filter(l => l.region === r)
          if (!regionListings.length) return null
          return (
            <div key={r}>
              <div style={{ padding:'24px 0 10px', fontFamily:'var(--mono)', fontSize:11, fontWeight:700, color:'var(--gray-500)', letterSpacing:2, textTransform:'uppercase', borderBottom:'1px solid var(--gray-200)', marginBottom:16, display:'flex', justifyContent:'space-between' }}>
                <span>{r}</span>
                <span style={{ fontWeight:400, fontSize:11 }}>{regionListings.length} listing{regionListings.length!==1?'s':''}</span>
              </div>
              <div className='cards-grid' style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:18, marginBottom:8 }}>
                {regionListings.map(l => <ListingCard key={l.id} listing={l} />)}
              </div>
            </div>
          )
        })}

        {/* National/online with no specific region */}
        {(() => {
          const rest = filtered.filter(l => !REGIONS.slice(1).includes(l.region))
          if (!rest.length) return null
          return (
            <div>
              <div style={{ padding:'24px 0 10px', fontFamily:'var(--mono)', fontSize:11, fontWeight:700, color:'var(--gray-500)', letterSpacing:2, textTransform:'uppercase', borderBottom:'1px solid var(--gray-200)', marginBottom:16 }}>
                National &amp; Online
              </div>
              <div className='cards-grid' style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:18 }}>
                {rest.map(l => <ListingCard key={l.id} listing={l} />)}
              </div>
            </div>
          )
        })()}
      </div>
    </div>
  )
}
